import { LocalStorageKeyEnum } from "../../enums/localStorage";

/**
 * Which EIP-6963 wallet the user selected — the record `getActiveEip1193()`
 * refuses against.
 *
 * DELIBERATELY IN ITS OWN FILE. These functions need nothing but `window` and one
 * enum, while `activeProvider.ts` imports the connector singleton and so drags in
 * the whole chains/connectors graph. Importing the helpers from there pulled that
 * graph into `useEagerConnect`, whose test mocks `config/chains` — and the suite
 * died at import time on `CHAIN_CONFIGS.entries()` before a single test ran. A
 * leaf this small should not carry that weight.
 *
 * TWO STORES, ON PURPOSE.
 *
 * `localStorage` is shared by every tab on this origin; the EIP-6963 connector is
 * per-tab module state. Keying the refusal off the shared store let one tab blind
 * another: connect plain MetaMask in tab A (no EIP-6963 provider there, correctly
 * and permanently), connect an EIP-6963 wallet in tab B, and tab A — properly
 * connected and able to sign — refused every order, deposit and signature with
 * "reconnect <rdns>", telling the user to reconnect a wallet already connected.
 * Traders keep tabs open.
 *
 * So the two readers want different scopes:
 *   - `localStorage`   : cross-tab and durable. What `useEagerConnect` restores from.
 *   - `sessionStorage` : THIS TAB only, and survives a reload. What the refusal
 *                        keys off, because "an EIP-6963 wallet is selected" is a
 *                        fact about a tab, not about the origin.
 *
 * Every accessor is wrapped: in private mode a storage call can throw, and the
 * safe direction is always "no record", which fails over instead of refusing.
 */

/** What the refusal reads: did THIS tab select an EIP-6963 wallet? */
export function readThisTabRdns(): string | null {
  try {
    return window.sessionStorage.getItem(LocalStorageKeyEnum.LAST_EIP6963_RDNS);
  } catch {
    return null;
  }
}

/** What eager reconnect reads: the durable, cross-tab record. */
export function readDurableRdns(): string | null {
  try {
    return window.localStorage.getItem(LocalStorageKeyEnum.LAST_EIP6963_RDNS);
  } catch {
    return null;
  }
}

/**
 * Record that an EIP-6963 wallet is the current selection, in both scopes.
 *
 * `useWallet.activate()` is the single writer, and clears on entry — see the note
 * there for why a second writer is what made this record unreliable before.
 */
export function rememberSelectedWallet(rdns: string): void {
  try {
    window.localStorage.setItem(LocalStorageKeyEnum.LAST_EIP6963_RDNS, rdns);
  } catch {
    // Blocked storage degrades to the fail-over, i.e. the old behaviour.
  }
  markSelectedWalletForThisTab(rdns);
}

/**
 * Arm the refusal for THIS tab without touching the shared store.
 *
 * `useEagerConnect` calls this when restoring a session on load: a freshly opened
 * tab has empty `sessionStorage`, so without it the refusal would be disarmed on
 * exactly the path that motivates it — a wallet uninstalled between sessions,
 * where no announcement matches and the connector is left empty.
 */
export function markSelectedWalletForThisTab(rdns: string): void {
  try {
    window.sessionStorage.setItem(LocalStorageKeyEnum.LAST_EIP6963_RDNS, rdns);
  } catch {
    // As above: an unwritable store reads back as "nothing selected".
  }
}

/**
 * Drop the record from both scopes, because an EIP-6963 wallet is no longer what
 * is selected.
 *
 * Called on disconnect and on entry to `activate()`. Without it, a user who once
 * used an EIP-6963 wallet and then connected Coinbase or WalletConnect would keep
 * a record naming a wallet with no provider behind it — `activate()` deactivates
 * the other connectors when one is already active, and
 * `EIP1193Connector.deactivate()` nulls its own provider — and be refused on every
 * write while correctly connected.
 */
export function forgetSelectedWallet(): void {
  try {
    window.localStorage.removeItem(LocalStorageKeyEnum.LAST_EIP6963_RDNS);
  } catch {
    // Nothing to do: an unreadable store also reads back as "no selection".
  }
  try {
    window.sessionStorage.removeItem(LocalStorageKeyEnum.LAST_EIP6963_RDNS);
  } catch {
    // As above.
  }
}
